import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { useAtom } from "jotai/index";
import { graphql } from "@repo/graphql-types/gql";
import { useEffect, type JSX } from "react";
import { z } from "zod";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { RecommendationMembersForPatientsForm } from "@/components/recommendation/recommendation-members-for-patient-form";
import { recommendationMemberAtom } from "@/lib/atoms/recommendation-member-atom.ts";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { recommendationPatientAtom } from "@/lib/atoms/recommendation-patient-atom";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RecommendationMemberForPatientConfirmationPageQuery = graphql(/* GraphQL */ `
  query RecommendationMemberForPatientConfirmationPageQuery(
    $codPaciente: Int!
    $codMembro: Int!
  ) {
    ...RecommendationMembersForPatientFormFragment
  }
`);

const searchSchema = z.object({
  patientId: z.number().optional(),
  userId: z.number().optional(),
});

const RecommendationMemberForPatientConfirmation = (): JSX.Element => {
  const params = useSearch({ from: "/recommendation/confirmation/" });

  const isMembersForPatientRecommendation = Boolean(params.patientId);

  const memberIdFromAtom = useAtom(recommendationMemberAtom)[0].id;
  const patientIdFromAtom = useAtom(recommendationPatientAtom)[0].id;

  const memberId = Number(
    isMembersForPatientRecommendation ? memberIdFromAtom : params.userId,
  );
  const patientId = Number(
    isMembersForPatientRecommendation ? params.patientId : patientIdFromAtom,
  );

  const router = useRouter();

  const queryResult = useGraphQL(RecommendationMemberForPatientConfirmationPageQuery, {
    codPaciente: patientId,
    codMembro: memberId,
  });

  const { data } = queryResult;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (patientId === 0 && !memberId) {
      router.history.go(-2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- se o member vira dependencia ele buga o reset do atom
  }, [patientId, router]);

  const onBackButtonClick = (): void => {
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton
          onClick={onBackButtonClick}
          icon={Left}
          align="start"
          data-testid="back-button"
        />
        <HeaderTitle title="Confirme os dados" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? <RecommendationMembersForPatientsForm data={data} /> : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};
export const Route = createFileRoute("/recommendation/confirmation/")({
  component: RecommendationMemberForPatientConfirmation,
  validateSearch: searchSchema,
});
